import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';

import BarLoaderComponent from 'webapp/components/bar-loader';
import BoardsPopupSelectListSingleItemComponent from 'webapp/components/boards/popup-select-list-single-item';
import CommonListAutoLoadedComponent from 'webapp/components/common/list/auto-loaded';
import FilterLineComponent from 'webapp/components/filter/line';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';

import type { BoardSelectListSingleItem } from 'webapp/models-custom/filter-sort/board-select-list-single';
import FilterSortBoardSelectListSingle from 'webapp/models-custom/filter-sort/board-select-list-single';

export default class BoardsPopupSelectListSingleComponent extends PopupInnerModalComponent<PopupData.Board.SelectListSingle> {
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/reports')
	declare mmReportsService: Service.ModelManager.Reports;

	@tracked isLoading = true;

	filterSort: FilterSortBoardSelectListSingle;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		void Promise.allSettled([
			this.mmBoardLoopsService.requestBoardLoops(),
			this.mmReportsService.requestReports(),
		]).then(() => {
			this.isLoading = false;
		});

		const filterSort = new FilterSortBoardSelectListSingle({
			getAllItems: () => {
				return this.model;
			},
		});
		this.filterSort = filterSort;
	}

	get isReport() {
		return this.popupData.isReport;
	}

	@cached
	get model() {
		if (this.isReport) {
			return [...this.mmReportsService.normalReportModels];
		} else {
			return [
				...this.mmBoardsService.normalBoardModels,
				...this.mmBoardLoopsService.normalBoardLoopModels,
			];
		}
	}

	get isEmpty(): boolean {
		return this.model.length === 0;
	}

	get emptyListText(): string {
		return `To add notifications, you need to create a ${this.isReport ? 'report' : 'dashboard'} first.`;
	}

	@action
	select(item: BoardSelectListSingleItem): void {
		this.popupData.onConfirm(item);
		this.close();
	}

	<template>
		<div>
			<FilterLineComponent @model={{this.filterSort}} />
			{{#if this.isLoading}}
				<div class="loader-ct">
					<BarLoaderComponent @theme="grey" />
				</div>
			{{else if this.isEmpty}}
				<p>{{this.emptyListText}}</p>
			{{else}}
				<FilterNoResultsComponent @model={{this.filterSort}} />
				<ul class="items">
					<CommonListAutoLoadedComponent
						@allItems={{this.filterSort.results}}
						@onVisibleItemsChanged={{this.reposition}}
						as |visibleItems|
					>
						{{#each visibleItems as |item|}}
							<BoardsPopupSelectListSingleItemComponent
								@item={{item}}
								@onSelect={{this.select}}
							/>
						{{/each}}
					</CommonListAutoLoadedComponent>
				</ul>
			{{/if}}
		</div>
	</template>
}
